/**
 * Lightweight signature management for Settings → Profile (ENG-3443).
 * Uses the same Basic-backed store as SignatureCaptureModal (`useUserSignature`).
 */
import { useMemo, useState } from 'react';
import type { Practitioner } from '@medplum/fhirtypes';
import { Button, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { notify } from '@/lib/notify';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import {
  isDrawnSignature,
  legalNameForSignature,
  SignatureCaptureModal,
  SignaturePreviewPanel,
} from '../../../components/SignatureCapture';
import { useUserSignature, type UserSignatureStatus } from '../../../hooks/useUserSignature';
import { formatPractitionerLicenseCredential } from '../../../lib/practitionerLicenseCredential';

export type SignatureCardState = 'loading' | 'unavailable' | 'empty-readonly' | 'preview' | 'editable';

export function signatureCardState({
  status,
  readOnly,
  hasSaved,
}: {
  status: UserSignatureStatus;
  readOnly: boolean;
  hasSaved: boolean;
}): SignatureCardState {
  if (status === 'error') return 'unavailable';
  if (status === 'loading') return 'loading';
  if (!readOnly) return 'editable';
  return hasSaved ? 'preview' : 'empty-readonly';
}

export function hasSavedSignature(saved: { signatureData?: string } | undefined): boolean {
  return Boolean(saved?.signatureData);
}

interface ProfileSignatureSettingsProps {
  practitionerId: string;
  practitioner: Practitioner | null;
  readOnly: boolean;
}

export function ProfileSignatureSettings({
  practitionerId,
  practitioner,
  readOnly,
}: ProfileSignatureSettingsProps): JSX.Element {
  const { config, status, saveConfig } = useUserSignature(practitionerId);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const saved = config.savedSignature;
  const hasSignature = hasSavedSignature(saved);
  const cardState = signatureCardState({ status, readOnly, hasSaved: hasSignature });
  const legalName = useMemo(
    () => legalNameForSignature(practitioner ?? undefined, saved?.name ?? ''),
    [practitioner, saved?.name],
  );
  const licenseLine = useMemo(
    () => formatPractitionerLicenseCredential(practitioner ?? undefined) ?? saved?.license,
    [practitioner, saved?.license],
  );
  const drawingSrc = isDrawnSignature(saved, config.preferredMode) && saved?.signatureData
    ? `data:${saved.signatureFormat ?? 'image/png'};base64,${saved.signatureData}`
    : undefined;

  const handleClear = async () => {
    setClearing(true);
    try {
      await saveConfig({ preferredMode: config.preferredMode, savedSignature: undefined });
      notify({
        title: 'Signature cleared',
        message: 'Your saved signature was removed.',
        type: 'success',
      });
    } catch {
      notify({
        title: 'Could not clear signature',
        message: 'Please try again.',
        type: 'error',
      });
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <Paper p={32} withBorder radius="md" shadow="sm" mt="lg">
        <Stack gap="md">
          <div>
            <Title order={4}>Signature</Title>
            <Text size="sm" c="dimmed">
              Saved signature used when signing clinical notes. Update it here or when signing a
              document.
            </Text>
          </div>
          {cardState === 'loading' ? (
            <Text size="sm" c="dimmed">
              Loading signature…
            </Text>
          ) : null}
          {cardState === 'unavailable' ? (
            <Text size="sm" c="dimmed">
              Signature unavailable.
            </Text>
          ) : null}
          {cardState === 'empty-readonly' ? (
            <Text size="sm" c="dimmed">
              No saved signature.
            </Text>
          ) : null}
          {cardState === 'preview' || cardState === 'editable' ? (
            <SignaturePreviewPanel
              name={legalName}
              license={licenseLine}
              signedAt={saved?.signedAt}
              drawingSrc={drawingSrc}
            />
          ) : null}
          {cardState === 'editable' ? (
            <Group gap="sm">
              <Button
                leftSection={<IconEdit size={16} />}
                color="gold"
                onClick={() => setCaptureOpen(true)}
              >
                {hasSignature ? 'Update signature' : 'Add signature'}
              </Button>
              {hasSignature ? (
                <Button
                  variant="light"
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  loading={clearing}
                  onClick={() => void handleClear()}
                >
                  Clear
                </Button>
              ) : null}
            </Group>
          ) : null}
        </Stack>
      </Paper>

      {cardState === 'editable' ? (
        <SignatureCaptureModal
          opened={captureOpen}
          onClose={() => setCaptureOpen(false)}
          allowSaveForFuture={false}
          title="Update signature"
          submitLabel="Save signature"
          onSign={async (signature) => {
            try {
              await saveConfig({ preferredMode: config.preferredMode, savedSignature: signature });
              setCaptureOpen(false);
              notify({
                title: 'Signature saved',
                message: 'Your signature will be available when signing notes.',
                type: 'success',
              });
            } catch {
              notify({
                title: 'Could not save signature',
                message: 'Please try again.',
                type: 'error',
              });
            }
          }}
        />
      ) : null}
    </>
  );
}
